import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { SegmentsTable } from "@/components/configuration/SegmentsTable";
import { Button } from "@/components/generic/Button";
import {
  getConfigurationCounts,
  getConfigurationSuggestions,
} from "@/queries/configurations";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions } from "@tanstack/react-router";
import { useMemo } from "react";

export const Route = createFileRoute(
  "/_dashboard/campaigns/setup/define/advanced/",
)({
  component: RouteComponent,
});

const linkContext = {
  editFilter: (category: string, filterId: string, quality: number) =>
    linkOptions({
      to: "/campaigns/setup/define/advanced/$quality/$category/$filter",
      params: { quality: quality, category, filter: filterId },
    }),
  contacts: (quality: number) =>
    linkOptions({
      to: "/campaigns/setup/define/advanced/$quality/$category/contacts",
      params: { category: "contacts", quality: quality },
    }),
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { useAdvancedConfigurations, useNewCampaignState } =
    Route.useRouteContext();
  const { newConfigurations, setNewConfigurations } =
    useAdvancedConfigurations();
  const [newCampaignState, setNewCampaignState] = useNewCampaignState();

  const goldConfig = newConfigurations.find((c) => c.quality === 1);

  // Re-use the cached suggestion data (staleTime: Infinity in the query def).
  // The /suggest response includes counts for ALL quality segments, even
  // disabled ones, unlike /counts which only covers enabled segments.
  const { data: suggestionsData } = useQuery(
    getConfigurationSuggestions({
      configuration: goldConfig,
    }),
  );

  // Enabled segments keep their own filters instead of the suggestion, so their
  // counts must come from /counts rather than the suggested configuration's count.
  const { data: countsData } = useQuery(
    getConfigurationCounts({
      configurations: newConfigurations,
      stopListIds:
        newCampaignState.stopLists?.map((s) => s.uniqueId.toString()) ?? [],
    }),
  );

  const externalCounts = useMemo(() => {
    if (!suggestionsData || !countsData) return undefined;
    const countsByQuality = new Map(
      countsData.map(({ quality, count }) => [quality, count]),
    );
    const enabledQualities = new Set(
      newConfigurations
        .filter((c) => c.enabled && !c.deleted)
        .map((c) => c.quality),
    );
    return new Map(
      suggestionsData.map((s) => {
        const quality = s.configuration.quality;
        return [
          quality,
          enabledQualities.has(quality)
            ? (countsByQuality.get(quality) ?? 0)
            : s.count,
        ];
      }),
    );
  }, [suggestionsData, countsData, newConfigurations]);

  const handleSave = () => {
    setNewCampaignState((prev) => ({
      ...prev,
      configurations: newConfigurations,
    }));
    navigate({ to: "/campaigns/setup/define", ignoreBlocker: true });
  };

  const handleCancel = () => {
    navigate({ to: "/campaigns/setup/define" });
  };

  return (
    <div className="flex flex-col gap-y-10">
      <SegmentHeader
        className="mx-auto max-w-122"
        title="Avanceret"
        description="Udforsk strategiske variationer af din målgruppe. Nedenfor ser du forslag til hvordan du kan variere målgruppen, og derved udvide din målgruppe."
      />

      <SegmentsTable
        configurations={newConfigurations}
        setConfigurations={setNewConfigurations}
        linkContext={linkContext}
        externalCounts={externalCounts}
        stopLists={newCampaignState.stopLists ?? []}
      />

      <div className="flex justify-center gap-6">
        <Button
          type="button"
          intent="secondary"
          className="h-12 min-w-37.5"
          animate
          onPress={handleCancel}
        >
          Annuller
        </Button>
        <Button
          type="button"
          className="h-12 min-w-37.5"
          animate
          onPress={handleSave}
        >
          Gem ændringer
        </Button>
      </div>
    </div>
  );
}
